// @ts-check
/* CUSTOM: UGC-videoer (sections/custom-ugc-videos.liquid).
   - Fliser: <video> uden src. IntersectionObserver sætter src (HLS i Safari/iOS, ellers mp4), når flisen nærmer sig,
     play() ved ≥ 50 % synlig, pause() når den forlader. Afvises play() (Low Power Mode, data saver)
     eller er reduced motion slået til, står posteren + et tyndt play-ikon (klassen is-paused).
   - Modal (<dialog>): én fælles <video>, som først får src ved åbning og nulstilles ved luk.
     Forrige/næste kører rundt, swipe + piletaster, Esc (keydown OG cancel), klik på baggrund lukker.
     Scroll-lås = html hidden + body clip (klassen custom-ugc-lock) – aldrig overflow:hidden på body.
   - Al markup kommer fra Liquid; JS viser/skjuler kun og sætter attributter.
   Ren logik er testbar via customElements.get('custom-ugc-videos').logic */
(function () {
  if (customElements.get('custom-ugc-videos')) return;

  /**
   * Indeks der kører rundt (efter sidste → første, før første → sidste).
   * @param {number} index
   * @param {number} count
   * @returns {number}
   */
  function wrapIndex(index, count) {
    if (!count || count < 1) return 0;
    return ((index % count) + count) % count;
  }

  /**
   * Hvilke pile skal være aktive under fliserne?
   * @param {number} scrollLeft
   * @param {number} scrollWidth
   * @param {number} clientWidth
   * @returns {{ prev: boolean, next: boolean, scrollable: boolean }}
   */
  function arrowState(scrollLeft, scrollWidth, clientWidth) {
    const max = scrollWidth - clientWidth;
    const tolerance = 2;
    return {
      prev: scrollLeft > tolerance,
      next: scrollLeft < max - tolerance,
      scrollable: max > tolerance,
    };
  }

  /**
   * Må en flise spille lige nu?
   * @param {{ ratio: number, autoplay: boolean, reducedMotion: boolean, modalOpen: boolean }} state
   * @returns {boolean}
   */
  function canAutoplay(state) {
    return Boolean(state.autoplay && !state.reducedMotion && !state.modalOpen && state.ratio >= 0.5);
  }

  /**
   * Retning for et swipe: 1 = næste, -1 = forrige, 0 = intet (for kort eller mest lodret).
   * @param {number} dx
   * @param {number} dy
   * @param {number} [threshold]
   * @returns {-1 | 0 | 1}
   */
  function swipeDirection(dx, dy, threshold = 40) {
    if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return 0;
    return dx < 0 ? 1 : -1;
  }

  /**
   * Videokilde: HLS hvor browseren selv kan afspille det (Safari/iOS – adaptiv bitrate),
   * ellers mp4 (hd før src). Bruges både til fliser og modal.
   * @param {{ hls?: string, hd?: string, src?: string }} sources
   * @param {boolean} canPlayHls
   * @returns {string}
   */
  function pickSource(sources, canPlayHls) {
    if (canPlayHls && sources.hls) return sources.hls;
    return sources.hd || sources.src || '';
  }

  /**
   * Fremdriftslinje: bredde og position af "tommelfingeren" (px) ud fra sliderens scroll.
   * @param {number} scrollLeft
   * @param {number} scrollWidth
   * @param {number} clientWidth
   * @param {number} barWidth
   * @returns {{ width: number, x: number }}
   */
  function progressState(scrollLeft, scrollWidth, clientWidth, barWidth) {
    if (!scrollWidth || !barWidth) return { width: barWidth, x: 0 };
    const width = Math.max(24, Math.round((clientWidth / scrollWidth) * barWidth));
    const max = Math.max(1, scrollWidth - clientWidth);
    const x = Math.round(Math.min(1, Math.max(0, scrollLeft / max)) * (barWidth - width));
    return { width, x };
  }

  /**
   * dataLayer-payload.
   * @param {string} event
   * @param {Record<string, unknown>} data
   */
  function trackPayload(event, data) {
    return { event, ugc_video: { ...data } };
  }

  class CustomUgcVideos extends HTMLElement {
    static logic = { wrapIndex, arrowState, canAutoplay, swipeDirection, pickSource, trackPayload, progressState };

    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.track = this.querySelector('[data-ugc-track]');
      /** @type {HTMLButtonElement[]} */
      this.tiles = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-ugc-tile]')]);
      /** @type {HTMLDialogElement | null} */
      this.modal = this.querySelector('[data-ugc-modal]');
      /** @type {HTMLVideoElement | null} */
      this.modalVideo = this.modal ? this.modal.querySelector('[data-ugc-modal-video]') : null;
      /** @type {HTMLElement[]} */
      this.slides = this.modal ? /** @type {HTMLElement[]} */ ([...this.modal.querySelectorAll('[data-ugc-slide]')]) : [];
      /** @type {HTMLButtonElement | null} */
      this.prevBtn = this.querySelector('[data-ugc-prev]');
      /** @type {HTMLButtonElement | null} */
      this.nextBtn = this.querySelector('[data-ugc-next]');
      /** @type {HTMLElement | null} */
      this.progress = this.querySelector('[data-ugc-progress]');
      /** @type {HTMLElement | null} */
      this.progressThumb = this.querySelector('[data-ugc-progress-thumb]');
      if (!this.track || !this.tiles.length) return;

      this.index = 0;
      this.isOpen = false;
      this.muted = true; // lyd fra som standard
      /** @type {HTMLElement | null} */
      this.trigger = null;
      /** @type {Map<Element, number>} */
      this.ratios = new Map();
      this.autoplay = this.dataset.autoplay === 'true';
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      const signal = this.abort.signal;

      // Fliser
      this.tiles.forEach((tile, i) => {
        tile.addEventListener('click', () => this.open(i, tile), { signal });
        const video = tile.querySelector('video');
        if (!video) return;
        video.addEventListener('playing', () => {
          tile.classList.add('is-playing');
          tile.classList.remove('is-paused');
        }, { signal });
        video.addEventListener('pause', () => tile.classList.remove('is-playing'), { signal });
        // iOS: 'playing' kan udeblive, når afspilning genoptages efter et afvist play() (fx efter modalen).
        // timeupdate kommer altid, mens videoen kører – så flisen viser aldrig play-ikonet oven på en kørende video.
        video.addEventListener('timeupdate', () => {
          if (video.paused) return;
          tile.classList.add('is-playing');
          tile.classList.remove('is-paused');
        }, { signal });
        video.addEventListener('error', () => {
          tile.classList.remove('is-playing');
          tile.classList.add('is-paused');
        }, { signal });
      });

      // Pile under fliserne
      this.prevBtn?.addEventListener('click', () => this.scrollTiles(-1), { signal });
      this.nextBtn?.addEventListener('click', () => this.scrollTiles(1), { signal });
      /** @type {number | undefined} */
      let frame;
      this.track.addEventListener('scroll', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.updateArrows());
      }, { passive: true, signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }
      this.updateArrows();
      this.setupDrag(signal);

      this.setupModal(signal);
      this.setupObservers();

      // Reduced motion ændret mens siden er åben
      this.reduced.addEventListener?.('change', () => this.syncAllTiles(), { signal });

      // Theme editor
      document.addEventListener('shopify:block:select', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (!this.contains(target)) return;
        const tile = /** @type {HTMLButtonElement | null} */ (target.querySelector('[data-ugc-tile]'));
        const i = tile ? this.tiles.indexOf(tile) : -1;
        if (i < 0 || !this.track) return;
        this.track.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
        this.open(i, tile);
      }, { signal });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(/** @type {Node} */ (e.target))) this.close();
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.loadObserver?.disconnect();
      this.playObserver?.disconnect();
      this.viewObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.modal?.open) this.modal.close();
      this.unlockScroll();
    }

    /* ---------- Fliser ---------- */

    setupObservers() {
      const allowed = this.autoplay && !this.reduced?.matches;
      if (!('IntersectionObserver' in window)) {
        this.tiles.forEach((t) => t.classList.add('is-paused'));
        return;
      }
      if (!allowed) this.tiles.forEach((t) => t.classList.add('is-paused'));

      // src sættes først, når flisen nærmer sig (200 px)
      this.loadObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          if (this.autoplay && !this.reduced?.matches) this.loadTile(/** @type {HTMLElement} */ (en.target));
          this.loadObserver?.unobserve(en.target);
        });
      }, { rootMargin: '200px' });

      // play ved ≥ 50 % synlig, pause når den forlader
      this.playObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          this.ratios?.set(en.target, en.intersectionRatio);
          this.syncTile(/** @type {HTMLElement} */ (en.target));
        });
      }, { threshold: [0, 0.5, 1] });

      this.tiles.forEach((t) => {
        this.loadObserver?.observe(t);
        this.playObserver?.observe(t);
      });

      // Ét view-event pr. sidevisning, når halvdelen af sektionen er synlig
      this.viewObserver = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        this.viewObserver?.disconnect();
        this.sendEvent('ugc_video_view', { count: this.tiles.length });
      }, { threshold: 0.5 });
      this.viewObserver.observe(this);
    }

    /** @param {HTMLElement} tile */
    loadTile(tile) {
      const video = tile.querySelector('video');
      if (!video || video.getAttribute('src')) return;
      const src = pickSource(
        { hls: video.dataset.srcHls, src: video.dataset.src },
        Boolean(video.canPlayType('application/vnd.apple.mpegurl'))
      );
      if (src) video.src = src;
    }

    /** @param {HTMLElement} tile */
    syncTile(tile) {
      const video = tile.querySelector('video');
      if (!video) return;
      const play = canAutoplay({
        ratio: this.ratios?.get(tile) || 0,
        autoplay: Boolean(this.autoplay),
        reducedMotion: Boolean(this.reduced?.matches),
        modalOpen: Boolean(this.modal?.open),
      });
      if (!play) {
        if (!video.paused) video.pause();
        if (!this.autoplay || this.reduced?.matches) tile.classList.add('is-paused');
        return;
      }
      this.loadTile(tile);
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // AbortError = pause() kom før afspilning startede – ikke en blokering
          if (err && err.name === 'AbortError') return;
          tile.classList.remove('is-playing');
          tile.classList.add('is-paused');
          this.retryLater(tile);
        });
      }
    }

    /**
     * Afvist play() (iOS lige efter modalen, Low Power Mode): prøv igen én gang kort efter
     * og ved næste berøring/scroll. Højst 2 tidsstyrede forsøg pr. flise, så det aldrig looper.
     * @param {HTMLElement} tile
     */
    retryLater(tile) {
      const tries = Number(tile.dataset.ugcRetries || 0);
      if (tries < 2) {
        tile.dataset.ugcRetries = String(tries + 1);
        window.setTimeout(() => this.syncTile(tile), 600);
      }
      if (this.retryBound) return;
      this.retryBound = true;
      const once = () => {
        this.retryBound = false;
        this.tiles.forEach((t) => { t.dataset.ugcRetries = '0'; });
        this.syncAllTiles();
      };
      const opts = { once: true, passive: true, signal: this.abort?.signal };
      document.addEventListener('touchstart', once, opts);
      document.addEventListener('pointerdown', once, opts);
      document.addEventListener('scroll', once, opts);
    }

    syncAllTiles() {
      this.tiles.forEach((t) => this.syncTile(t));
    }

    pauseTiles() {
      this.tiles.forEach((t) => {
        const video = t.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    }

    /** @param {number} direction -1 = tilbage, 1 = frem (én flise ad gangen) */
    scrollTiles(direction) {
      if (!this.track) return;
      const item = /** @type {HTMLElement | null} */ (this.track.querySelector('[data-ugc-item]'));
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: this.reduced?.matches ? 'auto' : 'smooth' });
    }

    updateArrows() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const state = arrowState(scrollLeft, scrollWidth, clientWidth);
      if (this.prevBtn) this.prevBtn.disabled = !state.prev;
      if (this.nextBtn) this.nextBtn.disabled = !state.next;
      this.toggleAttribute('data-scrollable', state.scrollable);
      // Fremdriftslinje: bredde = synlig andel, position = hvor langt der er scrollet
      if (this.progress && this.progressThumb && scrollWidth > 0) {
        const p = progressState(scrollLeft, scrollWidth, clientWidth, this.progress.clientWidth);
        this.progressThumb.style.width = p.width + 'px';
        this.progressThumb.style.transform = 'translateX(' + p.x + 'px)';
      }
    }

    /**
     * Træk med musen i slideren (desktop). Touch bruger browserens egen scroll.
     * Et træk må ikke åbne modalen: klik lige efter et træk afvises.
     * @param {AbortSignal} signal
     */
    setupDrag(signal) {
      const track = this.track;
      if (!track) return;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (!this.hasAttribute('data-scrollable')) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
      }, { signal });
      track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 4) {
          moved = true;
          try { track.setPointerCapture(e.pointerId); } catch (err) { /* ældre browsere */ }
        }
        if (moved) track.scrollLeft = startScroll - dx;
      }, { signal });
      const end = () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging'); // snap slår til igen og lægger sig til nærmeste flise
        if (moved) {
          this.dragMoved = true;
          window.setTimeout(() => { this.dragMoved = false; }, 0);
        }
      };
      track.addEventListener('pointerup', end, { signal });
      track.addEventListener('pointercancel', end, { signal });
      track.addEventListener('lostpointercapture', end, { signal });
      // Klik efter et træk åbner ikke modalen
      track.addEventListener('click', (e) => {
        if (!this.dragMoved) return;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true, signal });
    }

    /* ---------- Modal ---------- */

    /** @param {AbortSignal} signal */
    setupModal(signal) {
      const modal = this.modal;
      const video = this.modalVideo;
      if (!modal || !video) return;

      // Esc: lyt også på keydown – andre scripts på siden kan stoppe Esc, så 'cancel' aldrig kommer
      modal.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      }, { signal });
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          if (this.slides.length < 2) return;
          e.preventDefault();
          this.show(this.index + (e.key === 'ArrowRight' ? 1 : -1), 'keyboard');
        }
      }, { signal });
      // Oprydning uanset hvordan dialogen lukkes
      modal.addEventListener('close', () => this.onClosed(), { signal });

      modal.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target === modal) {
          this.close(); // klik på baggrunden
          return;
        }
        const product = /** @type {HTMLElement | null} */ (target.closest('[data-ugc-product]'));
        if (product) {
          const slide = this.slides[this.index];
          this.sendEvent('ugc_video_product_click', {
            handle: slide?.dataset.handle || '',
            product_id: product.dataset.productId || '',
            product_title: product.dataset.productTitle || '',
          });
          return;
        }
        const button = /** @type {HTMLElement | null} */ (target.closest('[data-ugc-action]'));
        if (!button) {
          // Klik/tap på selve videoen = pause/afspil (som PRYA). Knapperne håndteres nedenfor.
          if (target.closest('[data-ugc-player]')) {
            if (video.paused) this.playModal();
            else video.pause();
          }
          return;
        }
        switch (button.dataset.ugcAction) {
          case 'close':
            this.close();
            break;
          case 'prev':
            this.show(this.index - 1, 'button');
            break;
          case 'next':
            this.show(this.index + 1, 'button');
            break;
          case 'mute':
            this.toggleMute();
            break;
          case 'play':
            if (video.paused) this.playModal();
            else video.pause();
            break;
        }
      }, { signal });

      video.addEventListener('play', () => this.setPlayState(true), { signal });
      video.addEventListener('pause', () => this.setPlayState(false), { signal });

      // Swipe mellem videoer
      const player = modal.querySelector('[data-ugc-player]');
      if (player) {
        let startX = 0;
        let startY = 0;
        player.addEventListener('touchstart', (e) => {
          const t = /** @type {TouchEvent} */ (e).changedTouches[0];
          startX = t.clientX;
          startY = t.clientY;
        }, { passive: true, signal });
        player.addEventListener('touchend', (e) => {
          if (this.slides.length < 2) return;
          const t = /** @type {TouchEvent} */ (e).changedTouches[0];
          const dir = swipeDirection(t.clientX - startX, t.clientY - startY);
          if (dir) this.show(this.index + dir, 'swipe');
        }, { passive: true, signal });
      }
    }

    /**
     * @param {number} index
     * @param {HTMLElement | null} [trigger]
     */
    open(index, trigger = null) {
      const modal = this.modal;
      if (!modal || !this.modalVideo || !this.slides.length) return;
      if (!modal.open) {
        this.trigger = trigger || this.tiles[index] || null;
        modal.showModal();
        this.isOpen = true;
        this.lockScroll();
        this.pauseTiles();
        /** @type {HTMLElement | null} */ (modal.querySelector('[data-ugc-action="close"]'))?.focus({ preventScroll: true });
      }
      this.show(index, 'tile');
    }

    close() {
      if (this.modal?.open) this.modal.close();
      this.onClosed(); // med det samme – 'close'-eventet kan komme sent
    }

    // Oprydning – kører kun én gang pr. åbning (close() og 'close'-eventet kalder begge)
    onClosed() {
      if (!this.isOpen) return;
      this.isOpen = false;
      const video = this.modalVideo;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load(); // stopper download
      }
      this.unlockScroll();
      this.trigger?.focus({ preventScroll: true });
      this.syncAllTiles();
    }

    /**
     * @param {number} index
     * @param {string} source hvordan videoen blev vist (tile/button/keyboard/swipe)
     */
    show(index, source) {
      const video = this.modalVideo;
      if (!video || !this.slides.length) return;
      const i = wrapIndex(index, this.slides.length);
      this.index = i;
      this.slides.forEach((s, n) => {
        s.hidden = n !== i;
      });
      const slide = this.slides[i];
      const panel = slide.parentElement;
      if (panel) panel.scrollTop = 0;

      video.pause();
      video.poster = slide.dataset.poster || '';
      const src = pickSource(
        { hls: slide.dataset.srcHls, hd: slide.dataset.srcHd, src: slide.dataset.src },
        Boolean(video.canPlayType('application/vnd.apple.mpegurl'))
      );
      video.muted = Boolean(this.muted);
      this.setMuteState();
      if (src) {
        video.src = src;
        this.playModal();
      }

      const status = this.modal?.querySelector('[data-ugc-status]');
      if (status) status.textContent = slide.dataset.label || '';

      this.sendEvent('ugc_video_open', { handle: slide.dataset.handle || '', index: i + 1, source });
    }

    playModal() {
      const video = this.modalVideo;
      if (!video) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.setPlayState(false);
        });
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      if (this.modalVideo) this.modalVideo.muted = this.muted;
      this.setMuteState();
    }

    setMuteState() {
      const modal = this.modal;
      if (!modal) return;
      modal.classList.toggle('is-muted', Boolean(this.muted));
      const btn = modal.querySelector('[data-ugc-action="mute"]');
      btn?.setAttribute('aria-label', (this.muted ? this.dataset.labelSoundOn : this.dataset.labelSoundOff) || '');
    }

    /** @param {boolean} playing */
    setPlayState(playing) {
      const modal = this.modal;
      if (!modal) return;
      modal.classList.toggle('is-paused', !playing);
      const btn = modal.querySelector('[data-ugc-action="play"]');
      btn?.setAttribute('aria-label', (playing ? this.dataset.labelPause : this.dataset.labelPlay) || '');
    }

    lockScroll() {
      document.documentElement.classList.add('custom-ugc-lock');
    }

    unlockScroll() {
      document.documentElement.classList.remove('custom-ugc-lock');
    }

    /* ---------- Måling ---------- */

    /**
     * Send event til GTM (dataLayer) og Shopify Customer Events. Må aldrig stoppe siden.
     * @param {string} event
     * @param {Record<string, unknown>} data
     */
    sendEvent(event, data) {
      try {
        const payload = trackPayload(event, { ...data, source_product: this.dataset.sourceProduct || '' });
        /** @type {any} */
        const w = window;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ ecommerce: null });
        w.dataLayer.push(payload);
        w.Shopify?.analytics?.publish?.(event, payload);
      } catch (err) {
        /* tracking må aldrig stoppe siden */
      }
    }
  }

  customElements.define('custom-ugc-videos', CustomUgcVideos);
})();
